import { cn } from '@/lib/utils';
import {
  BookOpen,
  CreditCard,
  FileText,
  FolderOpen,
  LayoutDashboard,
  Lock,
  MessageCircle,
  Scale,
  Search,
  Settings,
  Shield,
  User,
  Zap
} from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth, useSubscription } from '@chainlit/react-client';

import UpgradeRequiredModal from '@/components/UpgradeRequiredModal';
import SidebarTrigger from '@/components/header/SidebarTrigger';
import { useTranslation } from '@/components/i18n/Translator';
import { Button } from '@/components/ui/button';
import { Sidebar, SidebarHeader, SidebarRail } from '@/components/ui/sidebar';

import NewChatButton from '../header/NewChat';
import SearchChats from './Search';
import { ThreadHistory } from './ThreadHistory';

function NavItem({
  icon,
  label,
  onClick,
  active,
  locked
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  locked?: boolean;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        'w-full justify-start h-8 px-2 text-sm font-normal gap-2',
        active
          ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
          : 'text-sidebar-foreground/80 hover:text-sidebar-foreground'
      )}
      onClick={onClick}
    >
      {icon}
      <span className="flex-1 text-left">{label}</span>
      {locked && <Lock className="w-3 h-3 shrink-0 opacity-50" />}
    </Button>
  );
}

export default function LeftSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isProUser } = useSubscription();
  const isAdmin =
    (user as any)?.metadata?.role === 'ADMIN' ||
    (user as any)?.role === 'ADMIN';
  const canAccess = isAdmin || isProUser;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const path = location.pathname;
  const hash = location.hash.replace('#', '');

  const is = (pathname: string) =>
    path === pathname || path.startsWith(pathname + '/');

  const isDashHash = (section: string) =>
    path === '/dashboard' && hash === section;

  return (
    <Sidebar {...props} className="border-none">
      <SidebarHeader className="py-3">
        <div className="flex items-center justify-between">
          <SidebarTrigger />
          <div className="flex items-center">
            <SearchChats />
            <NewChatButton navigate={navigate} />
          </div>
        </div>
      </SidebarHeader>

      {/* Main navigation */}
      <div className="px-3 pt-1 pb-2 flex flex-col gap-0.5">
        <NavItem
          icon={<MessageCircle className="w-4 h-4 shrink-0" />}
          label={t('navigation.header.chat')}
          onClick={() => navigate('/')}
          active={is('/') || is('/thread')}
        />
        <NavItem
          icon={<LayoutDashboard className="w-4 h-4 shrink-0" />}
          label={t('navigation.dashboard')}
          onClick={() => navigate('/dashboard')}
          active={path === '/dashboard' && hash === ''}
        />
        <NavItem
          icon={<FolderOpen className="w-4 h-4 shrink-0" />}
          label="Mina projekt"
          onClick={() => navigate('/projects')}
          active={is('/projects')}
        />
        <NavItem
          icon={<FileText className="w-4 h-4 shrink-0" />}
          label="Avtalsmallar"
          onClick={() => navigate('/mallar')}
          active={is('/mallar')}
        />
        <NavItem
          icon={<Scale className="w-4 h-4 shrink-0" />}
          label={t('navigation.eurlex')}
          onClick={() => canAccess ? navigate('/dashboard#eurlex') : setShowUpgradeModal(true)}
          active={isDashHash('eurlex')}
          locked={!canAccess}
        />
        <NavItem
          icon={<BookOpen className="w-4 h-4 shrink-0" />}
          label={t('navigation.dokument')}
          onClick={() => canAccess ? navigate('/dashboard#dokument') : setShowUpgradeModal(true)}
          active={isDashHash('dokument')}
          locked={!canAccess}
        />
        <NavItem
          icon={<Search className="w-4 h-4 shrink-0" />}
          label={t('dashboard.sidebar.navigation.caseLawSearch')}
          onClick={() => canAccess ? navigate('/dashboard#search') : setShowUpgradeModal(true)}
          active={isDashHash('search')}
          locked={!canAccess}
        />
      </div>

      {/* Divider */}
      <div className="mx-3 h-px bg-border" />

      {/* Account section */}
      <div className="px-3 pt-2 pb-1 flex flex-col gap-0.5">
        <NavItem
          icon={<User className="w-4 h-4 shrink-0" />}
          label={t('dashboard.sidebar.navigation.profile')}
          onClick={() => navigate('/dashboard#profile')}
          active={isDashHash('profile')}
        />
        <NavItem
          icon={<CreditCard className="w-4 h-4 shrink-0" />}
          label={t('dashboard.sidebar.navigation.billing')}
          onClick={() => navigate('/dashboard#billing')}
          active={isDashHash('billing')}
        />
      </div>

      {/* Upgrade CTA */}
      <div className="px-3 pb-2">
        <NavItem
          icon={<Zap className="w-4 h-4 shrink-0" />}
          label={t('dashboard.sidebar.navigation.upgrade')}
          onClick={() => navigate('/dashboard#upgrade')}
          active={isDashHash('upgrade')}
        />
      </div>

      {/* Admin section */}
      {isAdmin && (
        <>
          <div className="mx-3 h-px bg-border" />
          <div className="px-3 pt-2 pb-1 flex flex-col gap-0.5">
            <p className="px-2 pb-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
              Admin
            </p>
            <NavItem
              icon={<Shield className="w-4 h-4 shrink-0" />}
              label={t('dashboard.sidebar.navigation.users')}
              onClick={() => navigate('/dashboard#users')}
              active={isDashHash('users')}
            />
            <NavItem
              icon={<Settings className="w-4 h-4 shrink-0" />}
              label="Inställningar"
              onClick={() => navigate('/dashboard#mallar-payments')}
              active={isDashHash('mallar-payments')}
            />
          </div>
        </>
      )}

      <div className="mx-3 h-px bg-border" />

      <ThreadHistory />
      <SidebarRail />
      <UpgradeRequiredModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </Sidebar>
  );
}
